import { type AchievementRow } from '@busy/api';
import { ModalShell, useToast } from '@busy/ui';
import { useT } from '../lib/i18n';
import { ACHIEVEMENT_CATALOG, intensityForThreshold, thresholdLabel } from '../lib/achievements';
import { badgeArtworkUrl } from '../lib/badgeArtwork';
import { BadgeEffect } from './BadgeEffect';
import { KindIcon } from './CelebrationModal';
import { renderBadgeImage } from '../lib/badgeImage';

// Detail view for an already-unlocked achievement. Tapping a tile in the
// gallery opens this modal, which re-runs the kind-aware celebration
// animation at the unlocked threshold's intensity (1-8) so the user sees
// the same energy they got at unlock — and a stronger one if they're
// reviewing a high-tier badge. Shares the same hero treatment as
// CelebrationModal but drops the Next/multi-card scaffolding and adds the
// unlock timestamp.

export function AchievementDetailModal({
  achievement,
  onClose,
}: {
  achievement: AchievementRow;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const catalog = ACHIEVEMENT_CATALOG[achievement.kind];
  const thr = thresholdLabel(achievement.kind, achievement.threshold);
  const milestone = t(thr.key, thr.vars);
  const sub = t(`ach.sub.${achievement.kind}`);
  const intensity = intensityForThreshold(achievement.kind, achievement.threshold);

  // Tier index 0-3 for the small "Tier N" pill above the milestone label.
  const tierIdx = catalog.tiers.findIndex((tier) => tier.includes(achievement.threshold));
  const artwork = badgeArtworkUrl(achievement.kind, achievement.threshold);

  const earnedAt = new Date(achievement.earned_at);
  const earnedAtLabel = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(earnedAt);

  async function handleShare() {
    const title = t('ach.celebrate.shareTitle', { milestone });
    const body = t('ach.celebrate.shareBody', { milestone, sub });

    let file: File | null = null;
    try {
      const blob = await renderBadgeImage({
        kind: achievement.kind,
        threshold: achievement.threshold,
        milestone,
        sub,
        gradient: catalog.gradient,
      });
      file = new File([blob], `busy-${achievement.kind}-${achievement.threshold}.png`, {
        type: 'image/png',
      });
    } catch {
      file = null;
    }

    const nav = typeof navigator !== 'undefined' ? (navigator as Navigator) : null;
    if (nav?.share) {
      try {
        const payload: ShareData = { title, text: body };
        if (file && nav.canShare && nav.canShare({ files: [file] })) {
          payload.files = [file];
        }
        await nav.share(payload);
        return;
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(`${title}\n${body}`);
      toast(t('ach.celebrate.copied'), 'success');
    } catch {
      // no clipboard either — silent
    }
  }

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={milestone}>
      {/* Replays the kind's celebration at this threshold's intensity. Keyed
          on the achievement id so opening another badge restarts cleanly. */}
      <BadgeEffect key={achievement.id} kind={achievement.kind} intensity={intensity} />

      {artwork ? (
        <div className="relative overflow-hidden rounded-t-2xl bg-navy-900">
          {/* Hero artwork — square aspect to match the source files. */}
          <img
            src={artwork}
            alt=""
            className="w-full aspect-square object-cover"
            loading="eager"
          />
          {/* Bottom gradient + text so the milestone + tier read clearly
              over the artwork without needing a separate panel. */}
          <div className="absolute inset-x-0 bottom-0 px-7 pt-16 pb-7 text-center bg-gradient-to-t from-black/90 via-black/65 to-transparent text-white">
            {tierIdx >= 0 && (
              <div className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20">
                <span className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-white">
                  {t('ach.tier', { n: tierIdx + 1 })}
                </span>
              </div>
            )}
            <h2 className="font-display text-[32px] md:text-[40px] leading-[1.05] mb-1.5 drop-shadow">
              {milestone}
            </h2>
            <p className="text-white/85 text-sm max-w-xs mx-auto leading-relaxed">{sub}</p>
          </div>
        </div>
      ) : (
        <div className={`${catalog.gradient} text-white relative overflow-hidden rounded-t-2xl`}>
          <div className="relative z-[1] px-7 pt-12 pb-9 text-center">
            {tierIdx >= 0 && (
              <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20">
                <span className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-white">
                  {t('ach.tier', { n: tierIdx + 1 })}
                </span>
              </div>
            )}
            <div className="mx-auto w-24 h-24 rounded-3xl bg-white/15 backdrop-blur-sm grid place-items-center mb-5 border border-white/20 shadow-lg">
              <KindIcon kind={achievement.kind} className={`w-12 h-12 ${catalog.iconColor}`} />
            </div>
            <h2 className="font-display text-[36px] md:text-[44px] leading-[1.05] mb-2">
              {milestone}
            </h2>
            <p className="text-white/85 text-sm max-w-xs mx-auto leading-relaxed">{sub}</p>
          </div>
        </div>
      )}

      <div className="bg-surface px-5 py-4 border-t border-line">
        <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mb-1">
          {t('ach.detail.unlockedEyebrow')}
        </div>
        <div className="text-sm font-semibold text-ink-900">{earnedAtLabel}</div>
      </div>

      <div className="bg-bg-alt border-t border-line p-4 flex gap-2">
        <button
          type="button"
          onClick={handleShare}
          className="flex-1 h-11 rounded-lg border-2 border-navy-900 dark:border-gold-500 text-navy-900 dark:text-gold-500 font-semibold inline-flex items-center justify-center gap-2"
        >
          <ShareIcon className="w-4 h-4" />
          {t('ach.celebrate.share')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold"
        >
          {t('ach.detail.close')}
        </button>
      </div>
    </ModalShell>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}
